/**
 * PaymentAuthenticationModal — 3-D Secure step-up authentication.
 *
 * Rebuilt off the legacy the legacy overlay as part of the the legacy component retirement.
 * Shown while the bank holds the charge for additional verification — opens
 * the issuer's challenge URL in a new tab and watches `useHasFocus` so that
 * when the user returns to this tab we silently re-verify in the background.
 *
 * No `toggle` is passed to the Modal primitive — the dialog stays modal
 * until the user explicitly hits Cancel or the verify flow completes
 * (closing on the scrim would let them abandon the charge mid-3DS, which
 * is the actual bug the legacy code avoided by leaving toggle unwired).
 */

import React, {ReactElement, useEffect} from "react";
import useHasFocus from "@/hooks/useHasFocus";
import {Modal} from "@/widgets/Modal";
import "./PaymentAuthenticationModal.scss";

interface Props {
    isOpen: boolean;
    onVerify: (displayError: boolean) => void;
    onClose: () => void;
    paymentNextAction: string;
}

function PaymentAuthenticationModal(props: Props): ReactElement {
    const hasFocus = useHasFocus();

    /* Silent re-verify when the user tabs back from the issuer's challenge. */
    useEffect(() => {
        if (hasFocus && props.isOpen) {
            props.onVerify(false);
        }
    }, [hasFocus]);

    /* Auto-open the issuer's challenge URL in a new tab on first render. */
    useEffect(() => {
        if (props.isOpen && props.paymentNextAction) {
            window.open(props.paymentNextAction, "_blank")?.focus();
        }
    }, [props.isOpen, props.paymentNextAction]);

    return (
        <Modal
            isOpen={props.isOpen}
            disableOverlayDismiss={true}
            size="xs"
            contentClassName="pay-auth-modal"
            ariaLabel="Payment Processing"
        >
            <header className="pay-auth-modal__head">
                <span className="pay-auth-modal__eyebrow">
                    <span className="pay-auth-modal__dot" aria-hidden="true" />
                    Payment Processing
                </span>
            </header>

            <h2 className="pay-auth-modal__title">3-D Secure</h2>

            <div className="pay-auth-modal__body">
                <p className="pay-auth-modal__copy">
                    For your security, your bank has requested additional authentication before completing your purchase.
                </p>
                <p className="pay-auth-modal__copy">
                    If the issuer&rsquo;s window didn&rsquo;t open, click below to launch it.
                </p>
            </div>

            <div className="pay-auth-modal__cta-stack">
                <a
                    className="pay-auth-modal__primary"
                    href={props.paymentNextAction}
                    target="_blank"
                    rel="noreferrer"
                >
                    Authenticate Payment
                </a>

                <button
                    type="button"
                    className="pay-auth-modal__verify"
                    onClick={() => props.onVerify(true)}
                >
                    I&rsquo;ve Authenticated — Verify Now
                </button>

                <button
                    type="button"
                    className="pay-auth-modal__cancel"
                    onClick={props.onClose}
                >
                    Cancel
                </button>
            </div>
        </Modal>
    );
}

export default PaymentAuthenticationModal;
